import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useRelatedEntityDraftFetchMutation,
  useBusinessRelationDraftFetchMutation,
  useUpdateRelatedEntityMutation,
  useUpdateBusinessRelationMutation,
  useDeleteRelatedEntityDraftMutation,
  useDeleteBusinessRelationDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { CommonToast } from "../../../common/Toast";
import { liveRelations, mergeRelationDrafts, relationOf } from "./relationFields";

/* The two relation sections' data — one implementation for both. */
export const useRelationSection = (spec, { onStatusChange } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [draftRows, setDraftRows] = useState([]);

  const [fetchEntityDrafts] = useRelatedEntityDraftFetchMutation();
  const [fetchBusinessDrafts] = useBusinessRelationDraftFetchMutation();
  const [updateEntity] = useUpdateRelatedEntityMutation();
  const [updateBusiness] = useUpdateBusinessRelationMutation();
  const [deleteEntityDraft] = useDeleteRelatedEntityDraftMutation();
  const [deleteBusinessDraft] = useDeleteBusinessRelationDraftMutation();

  const isEntity = spec.key === "entity";
  const fetchDrafts = isEntity ? fetchEntityDrafts : fetchBusinessDrafts;
  const softDelete = isEntity ? updateEntity : updateBusiness;
  const deleteDraft = isEntity ? deleteEntityDraft : deleteBusinessDraft;

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await fetchDrafts({ companyID, token });
      /* Transcribed: both sections read the nested `data.data`. */
      setDraftRows(mergeRelationDrafts(res?.data?.data || [], currentEditingCompany, spec));
    } catch {
      /* The live calls are unguarded, so a failure became an unhandled
         rejection. Falls back to what is on record. */
      setDraftRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token, currentEditingCompany, spec.key]);

  useEffect(() => {
    if (!currentEditingCompany) return;
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany, spec.key]);

  const usingDrafts = draftRows.length > 0;
  const rows = usingDrafts
    ? draftRows
    : liveRelations(currentEditingCompany, spec).map((row) => relationOf(row, spec));

  const refresh = () => {
    /* A bare refetch() promise, rejected, surfaces as an unhandled rejection
       the dev overlay reports as "[object Object]". */
    Promise.resolve(refetch()).catch(() => {});
    loadDrafts();
  };

  /* Transcribed: a draft row is deleted outright and a null body counts as
     success; a live row is SOFT-deleted with is_deleted: 1. */
  const remove = async (record) => {
    try {
      if (record?.[spec.draftIdKey]) {
        const res = await deleteDraft({ data: record[spec.draftIdKey] });
        if (res?.data == null) {
          CommonToast.success(spec.deleteMessage);
          refresh();
        }
        return;
      }

      if (record?.[spec.idKey]) {
        const res = await softDelete({
          data: { is_deleted: 1 },
          [spec.idArg]: record[spec.idKey],
          /* The same permission trap as the ownership delete: these endpoints
             resolve the company from a chain that ends in kwargs['pk'], so
             without a company reference a non-admin is refused. See F-062. */
          token,
        }).unwrap();

        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success(spec.deleteMessage);
          refresh();
        }
      }
    } catch (error) {
      /* The live handlers report with alert() and have no catch on the
         unwrapped call. DRF's refusal arrives as `data.detail`. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't delete this relation."
      );
    }
  };

  return { rows, usingDrafts, remove, refresh };
};

export default useRelationSection;
